import type { GetLongQuestionnaireSummaryReportQuery } from '@report/graphql';

import {
  AGE_LABELS,
  buildSection,
  EDUCATION_LABELS,
  mapSection,
  ORGANIZATION_LABELS,
  placeholderSection,
  type QuestionnaireReportSection,
  RATING_LABELS,
  type SectionConfig,
} from '../questionnaire-report-shared';

export type LongFormSection = QuestionnaireReportSection;

type LongQuestionnaireSummaryReport =
  GetLongQuestionnaireSummaryReportQuery['reportGetLongQuestionnaireSummaryReport'];

// The Long exam form offers a wider set of majors than Standard/Short, in the
// order shown in the "Template 3 – Long Form" design.
export const LONG_MAJOR_LABELS: Record<string, string> = {
  financial: 'การเงิน / การธนาคาร / วิศวกรรมการเงิน',
  accounting: 'การบัญชี',
  mathematics: 'คณิตศาสตร์ / สถิติ / คณิตศาสตร์ประกันภัย (Actuarial Science)',
  economics: 'เศรษฐศาสตร์',
  'business-administration': 'บริหารธุรกิจสาขาอื่น (การตลาด / การจัดการ / ทรัพยากรบุคคล)',
  'computer-science': 'วิทยาการคอมพิวเตอร์ / เทคโนโลยีสารสนเทศ / วิศวกรรมคอมพิวเตอร์',
  engineering: 'วิศวกรรมศาสตร์สาขาอื่น (ไฟฟ้า / โยธา / เครื่องกล ฯลฯ) / สถาปัตยกรรมศาสตร์',
  'natural-science': 'วิทยาศาสตร์ธรรมชาติ (เคมี / ฟิสิกส์ / ชีววิทยา)',
  'health-science': 'วิทยาศาสตร์สุขภาพ (แพทยศาสตร์ / พยาบาลศาสตร์ / เภสัชศาสตร์ ฯลฯ)',
  'social-science': 'สังคมศาสตร์ / มนุษยศาสตร์ / ศึกษาศาสตร์ (นิติศาสตร์ / รัฐศาสตร์ / ภาษา ฯลฯ)',
  other: 'อื่นๆ (โปรดระบุ)',
};

export const OCCUPATION_LABELS: Record<string, string> = {
  student: 'นิสิต / นักศึกษา',
  'company-employee': 'พนักงานบริษัท',
  'business-owner': 'เจ้าของธุรกิจ',
  freelance: 'อาชีพอิสระ',
  government: 'ข้าราชการ / พนักงานรัฐวิสาหกิจ',
  other: 'อื่น ๆ โปรดระบุ',
};

export const WORK_EXPERIENCE_LABELS: Record<string, string> = {
  none: 'ไม่มีประสบการณ์ทำงานในธุรกิจหลักทรัพย์',
  'less-than-1-year': 'น้อยกว่า 1 ปี',
  '1-to-3-years': '1 - 3 ปี',
  '4-to-7-years': '4 - 7 ปี',
  'more-than-7-years': 'มากกว่า 7 ปี',
};

export const EXAM_ATTEMPTED_LABELS: Record<string, string> = {
  never: 'ไม่เคย',
  'more-than-zero': 'เคย (ระบุจำนวนครั้ง)',
};

export const LONG_EXAM_PREPARATION_LABELS: Record<string, string> = {
  'prepare-exam-by-self': 'เตรียมตัวสอบด้วยตนเอง',
  'attend-training-from-official': 'เข้าอบรมหลักสูตรเตรียมสอบที่จัดโดยหน่วยงานเจ้าของหลักสูตร',
  'attend-training-from-training-institution':
    'เข้าอบรมหลักสูตรเตรียมสอบกับสถาบันอบรม / มหาวิทยาลัย หรือ หน่วยงานอื่น ๆ',
  'attend-specific-program': 'เข้าอบรมในโครงการเฉพาะ เช่น New Breed, TFEX Academy, ESG Academy',
  other: 'อื่น ๆ โปรดระบุ',
};

// The Long exam form's objectives, including `career-opportunity` (unlike
// Standard/Short), in the order shown in the "Template 3 – Long Form" design.
export const LONG_OBJECTIVE_LABELS: Record<string, string> = {
  'career-opportunity': 'เพื่อสร้างโอกาสด้านอาชีพ / การสมัครงาน',
  'register-license-and-profession': 'เพื่อขึ้นทะเบียนใบอนุญาตฯ / ใบประกอบวิชาชีพ',
  'self-assessment': 'เพื่อประเมินความรู้ตนเอง',
  'special-project-with-outside-institution':
    'สอบตามเงื่อนไขของโครงการเฉพาะของหน่วยงานภายนอก / สถาบันการศึกษา',
  other: 'อื่น ๆ โปรดระบุ',
};

export const DEVICE_LABELS: Record<string, string> = {
  laptop: 'Laptop',
  pc: 'PC',
  'tablet-ipad': 'Tablet ประเภท iPad',
  'tablet-android': 'Tablet ประเภท Android, Huawei, อื่นๆ',
  other: 'อื่น ๆ โปรดระบุ',
};

export const REFERRAL_SOURCE_LABELS: Record<string, string> = {
  'social-media': 'Social Media',
  'friend-recommendation': 'เพื่อนแนะนำ',
  'organization-or-institution': 'หน่วยงาน / สถาบันการศึกษา',
  'email-pr': 'อีเมลประชาสัมพันธ์',
  'website-announcement': 'ประกาศจาก Website',
  other: 'อื่น ๆ โปรดระบุ',
};

export const READINESS_LABELS: Record<string, string> = {
  most: 'มากที่สุด',
  high: 'มาก',
  low: 'น้อย',
  least: 'น้อยที่สุด',
};

// Maps every exam-room questionnaire province code (see
// `EXAM_PROVINCE_CODES` in `apps/exam-room/.../questionnaire-form/constants.ts`)
// to its Thai name (see `questionnaire.examProvince.option.<code>` in
// `libs/exam-room/translations/src/locales/th.json`). Used only to translate
// the province codes the API actually returns — see `buildProvinceSection`.
export const PROVINCE_LABELS: Record<string, string> = {
  '10': 'กรุงเทพมหานคร',
  '11': 'สมุทรปราการ',
  '12': 'นนทบุรี',
  '13': 'ปทุมธานี',
  '14': 'พระนครศรีอยุธยา',
  '15': 'อ่างทอง',
  '16': 'ลพบุรี',
  '17': 'สิงห์บุรี',
  '18': 'ชัยนาท',
  '19': 'สระบุรี',
  '20': 'ชลบุรี',
  '21': 'ระยอง',
  '22': 'จันทบุรี',
  '23': 'ตราด',
  '24': 'ฉะเชิงเทรา',
  '25': 'ปราจีนบุรี',
  '26': 'นครนายก',
  '27': 'สระแก้ว',
  '30': 'นครราชสีมา',
  '31': 'บุรีรัมย์',
  '32': 'สุรินทร์',
  '33': 'ศรีสะเกษ',
  '34': 'อุบลราชธานี',
  '35': 'ยโสธร',
  '36': 'ชัยภูมิ',
  '37': 'อำนาจเจริญ',
  '39': 'หนองบัวลำภู',
  '40': 'ขอนแก่น',
  '41': 'อุดรธานี',
  '42': 'เลย',
  '43': 'หนองคาย',
  '44': 'มหาสารคาม',
  '45': 'ร้อยเอ็ด',
  '46': 'กาฬสินธุ์',
  '47': 'สกลนคร',
  '48': 'นครพนม',
  '49': 'มุกดาหาร',
  '50': 'เชียงใหม่',
  '51': 'ลำพูน',
  '52': 'ลำปาง',
  '53': 'อุตรดิตถ์',
  '54': 'แพร่',
  '55': 'น่าน',
  '56': 'พะเยา',
  '57': 'เชียงราย',
  '58': 'แม่ฮ่องสอน',
  '60': 'นครสวรรค์',
  '61': 'อุทัยธานี',
  '62': 'กำแพงเพชร',
  '63': 'ตาก',
  '64': 'สุโขทัย',
  '65': 'พิษณุโลก',
  '66': 'พิจิตร',
  '67': 'เพชรบูรณ์',
  '70': 'ราชบุรี',
  '71': 'กาญจนบุรี',
  '72': 'สุพรรณบุรี',
  '73': 'นครปฐม',
  '74': 'สมุทรสาคร',
  '75': 'สมุทรสงคราม',
  '76': 'เพชรบุรี',
  '77': 'ประจวบคีรีขันธ์',
  '80': 'นครศรีธรรมราช',
  '81': 'กระบี่',
  '82': 'พังงา',
  '83': 'ภูเก็ต',
  '84': 'สุราษฎร์ธานี',
  '85': 'ระนอง',
  '86': 'ชุมพร',
  '90': 'สงขลา',
  '91': 'สตูล',
  '92': 'ตรัง',
  '93': 'พัทลุง',
  '94': 'ปัตตานี',
  '95': 'ยะลา',
  '96': 'นราธิวาส',
  '97': 'บึงกาฬ',
};

type SectionField = Exclude<keyof LongQuestionnaireSummaryReport, '__typename'>;

const PROVINCE_SECTION_OPTIONS = {
  key: 'section-4',
  title: '4 สถานที่สอบตั้งอยู่จังหวัดใด',
  variant: 'standard' as const,
};

// Shown in the province table body when the report loads with no province rows
// matching the selected filters (the province section is data-driven, so an
// empty result is expected rather than a zero-filled known option set).
const PROVINCE_EMPTY_MESSAGE = 'ไม่มีข้อมูลจังหวัดตามเงื่อนไขที่เลือก';

const TOP_SECTION_CONFIGS: SectionConfig<SectionField>[] = [
  { key: 'section-1', variant: 'standard', title: '1 อายุ', field: 'age', labelMap: AGE_LABELS },
  {
    key: 'section-2',
    variant: 'standard',
    title: '2 ระดับการศึกษาสูงสุด',
    field: 'highestEducationLevel',
    labelMap: EDUCATION_LABELS,
  },
  {
    key: 'section-3',
    variant: 'standard',
    title: '3 ภูมิหลังเกี่ยวกับสาขาที่กำลังศึกษา หรือจบการศึกษาล่าสุด',
    field: 'major',
    labelMap: LONG_MAJOR_LABELS,
  },
  // section-4 (examProvince) is handled separately by `buildProvinceSection`
  // — see the special handling note there.
  {
    key: 'section-5',
    variant: 'standard',
    title: '5 อาชีพปัจจุบัน',
    field: 'currentOccupation',
    labelMap: OCCUPATION_LABELS,
  },
  {
    key: 'section-6',
    variant: 'standard',
    title: '6 ประเภทหน่วยงานที่ท่านทำงานอยู่ในปัจจุบัน',
    field: 'currentOrganization',
    labelMap: ORGANIZATION_LABELS,
  },
  {
    key: 'section-7',
    variant: 'standard',
    title: '7 ประสบการณ์ทำงานที่เกี่ยวข้องกับวิชาชีพในธุรกิจหลักทรัพย์',
    field: 'workExperience',
    labelMap: WORK_EXPERIENCE_LABELS,
  },
  {
    key: 'section-8',
    variant: 'standard',
    title: '8 ท่านเคยสอบหลักสูตรนี้มาก่อนหรือไม่',
    field: 'examAttempted',
    labelMap: EXAM_ATTEMPTED_LABELS,
  },
  {
    key: 'section-9',
    variant: 'standard',
    title: '9 วิธีการในการเตรียมตัวสอบ',
    field: 'examPreparations',
    labelMap: LONG_EXAM_PREPARATION_LABELS,
  },
  {
    key: 'section-10',
    variant: 'standard',
    title: '10 วัตถุประสงค์ของการเข้าสอบ',
    field: 'examAttendingObjectives',
    labelMap: LONG_OBJECTIVE_LABELS,
  },
  {
    key: 'section-11',
    variant: 'standard',
    title: '11 อุปกรณ์ที่ใช้ในการสอบ',
    field: 'examDevice',
    labelMap: DEVICE_LABELS,
  },
  {
    key: 'section-12',
    variant: 'standard',
    title: '12 ท่านรู้จักหลักสูตรนี้จากที่ไหน',
    field: 'referralSource',
    labelMap: REFERRAL_SOURCE_LABELS,
  },
  {
    key: 'section-13',
    variant: 'standard',
    title: '13 ความพร้อมในการสอบครั้งนี้อยู่ระดับใด',
    field: 'examReadiness',
    labelMap: READINESS_LABELS,
  },
];

const SATISFACTION_SUBSECTION_CONFIGS: SectionConfig<SectionField>[] = [
  {
    key: 'section-14-1',
    variant: 'standard',
    title: '14.1 ด้านสถานที่',
    field: 'locationSatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-14-2',
    variant: 'standard',
    title: '14.2 ด้านการให้บริการของเจ้าหน้าที่',
    field: 'staffServicesSatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-14-3',
    variant: 'standard',
    title: '14.3 ด้านระบบจัดทดสอบ — ความสะดวกและเข้าใจง่ายในการใช้งาน',
    field: 'systemUsabilitySatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-14-4',
    variant: 'standard',
    title: '14.4 ด้านระบบจัดทดสอบ — ความเสถียรและความต่อเนื่องของระบบ',
    field: 'systemStabilitySatisfaction',
    labelMap: RATING_LABELS,
  },
];

/**
 * The province section (section-4 / `examProvince`) differs from every other
 * section: instead of a fixed known option set, the report shows ONLY the
 * provinces the API actually returned, sorted alphabetically by their Thai
 * label (not restricted/zero-filled to the full 77-province list). While
 * loading (`report` undefined), it renders with empty rows (not a 77-row
 * placeholder) so only a title + Total skeleton shows.
 */
function buildProvinceSection(report?: LongQuestionnaireSummaryReport): QuestionnaireReportSection {
  if (!report) {
    return {
      key: PROVINCE_SECTION_OPTIONS.key,
      variant: PROVINCE_SECTION_OPTIONS.variant,
      title: PROVINCE_SECTION_OPTIONS.title,
      rows: [],
      total: placeholderSection(PROVINCE_LABELS, PROVINCE_SECTION_OPTIONS).total,
      emptyMessage: PROVINCE_EMPTY_MESSAGE,
    };
  }

  const section = mapSection(report.examProvince, PROVINCE_LABELS, PROVINCE_SECTION_OPTIONS, false);
  return {
    ...section,
    rows: [...section.rows].sort((a, b) => a.label.localeCompare(b.label, 'th')),
    emptyMessage: PROVINCE_EMPTY_MESSAGE,
  };
}

/**
 * Builds the ordered Long Form sections. When `report` is undefined (query
 * still loading), a placeholder scaffold is returned so the table stays
 * visible and the loading skeleton can render.
 *
 * Every section except province (section-4) is restricted to its known
 * option set (`restrictToKnownOptions`), so the report shows exactly the
 * options in the "Template 3 – Long Form" design even though the backend may
 * bucket some questions by a wider taxonomy — any option key outside the
 * label map is dropped. Province is data-driven — see `buildProvinceSection`.
 */
export function buildLongFormSections(report?: LongQuestionnaireSummaryReport): LongFormSection[] {
  const restrict = (config: SectionConfig<SectionField>): SectionConfig<SectionField> => ({
    ...config,
    restrictToKnownOptions: true,
  });

  const [firstThree, restTop] = [TOP_SECTION_CONFIGS.slice(0, 3), TOP_SECTION_CONFIGS.slice(3)];

  return [
    ...firstThree.map((config) => buildSection(restrict(config), report)),
    buildProvinceSection(report),
    ...restTop.map((config) => buildSection(restrict(config), report)),
    {
      key: 'section-14',
      variant: 'rating',
      title: '14 ความพึงพอใจต่อการใช้บริการสอบ',
      rows: [],
      subSections: SATISFACTION_SUBSECTION_CONFIGS.map((config) =>
        buildSection(restrict(config), report),
      ),
    },
  ];
}
